import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import {
  buildEarningsView,
  type EarningsPeriod,
  type EarningsView,
  type EntityGrain,
} from './earningsModel';
import type {
  AffiliateNetwork,
  Merchant,
  MerchantRegion,
  Transaction,
  WelcomeBonusFunding,
} from '../dataProvider/types';

/**
 * Region-driven Earnings view, read through the earnings aggregation seam
 * (`buildEarningsView`), NOT computed in the component. For v1 it pulls the raw
 * collections from the mock provider and aggregates client-side; the real-data
 * migration replaces the fetch-and-aggregate here with a single server-side
 * earnings endpoint and the surface does not change (D-earnings).
 *
 * Scope comes from the region context (the SCOPE, UK | US | GLOBAL): the surface
 * shows every band the scope derives to at once, so it keys off `region`, not the
 * switcher's `activeRegion`. `now` is injected so the period windows and the
 * trend are deterministic under test.
 */

// One "give me everything" page; the mock is fully in-memory and small.
const ALL = {
  pagination: { page: 1, perPage: 100_000 },
  sort: { field: 'id', order: 'ASC' as const },
  filter: {},
};

export const useEarnings = (opts: {
  grain: EntityGrain;
  period: EarningsPeriod;
  /** Reference clock (epoch ms) for the period windows and the trend. */
  now: number;
}): { view: EarningsView | null; isLoading: boolean } => {
  const dataProvider = useDataProvider();
  const { region } = useRegion();

  const { data, isLoading } = useQuery({
    queryKey: ['earnings', region, opts.grain, opts.period, opts.now],
    queryFn: async () => {
      const [
        transactions,
        merchants,
        merchantRegions,
        affiliateNetworks,
        welcomeBonusFundings,
      ] = await Promise.all([
        dataProvider.getList('transactions', ALL),
        dataProvider.getList('merchants', ALL),
        dataProvider.getList('merchantRegions', ALL),
        dataProvider.getList('affiliateNetworks', ALL),
        dataProvider.getList('welcomeBonusFundings', ALL),
      ]);
      return buildEarningsView(
        {
          transactions: transactions.data as unknown as Transaction[],
          merchants: merchants.data as unknown as Merchant[],
          merchantRegions: merchantRegions.data as unknown as MerchantRegion[],
          affiliateNetworks:
            affiliateNetworks.data as unknown as AffiliateNetwork[],
          welcomeBonusFundings:
            welcomeBonusFundings.data as unknown as WelcomeBonusFunding[],
        },
        {
          scope: region,
          grain: opts.grain,
          period: opts.period,
          now: opts.now,
        },
      );
    },
  });

  return { view: data ?? null, isLoading };
};
